/**
 * Admin TOTP self-management screen. Closes the Phase 7 P0 deferral
 * (enrollment) + the follow-up for rotate + disable.
 *
 * Three flows live on this screen:
 *
 *   - **Enroll** (idle → enrolling → activated): the initial signup,
 *     surfaces the recovery codes ONCE at the end.
 *   - **Rotate** (idle → rotating → activated): the admin already has
 *     TOTP; they want to switch to a new authenticator. Requires the
 *     CURRENT code + a freshly-generated secret + a code from the new
 *     authenticator. Re-emits 10 fresh recovery codes (old set is
 *     invalidated server-side).
 *   - **Disable** (single form): turn TOTP off. Requires the current
 *     code or a recovery code. Useful when an admin gets a new device
 *     and wants to re-enroll cleanly.
 *
 * Each mutation IS the recent-verify gate: the backend requires the
 * current TOTP code in the request body, so an attacker who steals
 * the session token can't rotate/disable without also having the
 * authenticator.
 *
 * The QR code ships as a manual-key + otpauth-URI fallback panel —
 * every authenticator supports manual entry, and a real <canvas>/<svg>
 * QR generator can land later without changing this screen's shape.
 */

import { useState } from 'react';
import {
  useTotpActivateMutation,
  useTotpDisableMutation,
  useTotpEnrollMutation,
  useTotpRotateMutation,
} from '../api/totp.js';

type ViewState =
  | { kind: 'idle' }
  | {
      kind: 'enrolling';
      secretBase32: string;
      otpauthUri: string;
    }
  | {
      kind: 'activated';
      recoveryCodes: string[];
    };

export function AccountTotpRoute() {
  const [state, setState] = useState<ViewState>({ kind: 'idle' });
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const enroll = useTotpEnrollMutation();
  const activate = useTotpActivateMutation();

  return (
    <section className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold text-neutral-900">
          Two-factor authentication
        </h1>
        <p className="mt-1 text-sm text-neutral-600">
          TOTP is mandatory for every admin account in production. After enrolling, save the 10
          recovery codes somewhere safe — they're the only path back in if you lose your
          authenticator.
        </p>
      </header>

      {state.kind === 'idle' && (
        <div className="space-y-3">
          <button
            type="button"
            disabled={enroll.isPending}
            onClick={async () => {
              setError(null);
              try {
                const res = await enroll.mutateAsync();
                setState({
                  kind: 'enrolling',
                  secretBase32: res.secretBase32,
                  otpauthUri: res.otpauthUri,
                });
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Could not start enrollment');
              }
            }}
            className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {enroll.isPending ? 'Issuing secret…' : 'Enroll TOTP'}
          </button>
          {error && <p className="text-sm text-danger-600">{error}</p>}
        </div>
      )}

      {state.kind === 'enrolling' && (
        <div className="space-y-4 rounded-md border border-neutral-200 bg-white p-4">
          <Section title="1. Scan with your authenticator">
            <p className="text-sm text-neutral-700">
              Open Google Authenticator / 1Password / Authy / Bitwarden and scan the QR. If your app
              doesn't support QR scans, paste the manual key below.
            </p>
            <div className="mt-3 flex items-start gap-4">
              <QrFallback uri={state.otpauthUri} />
              <div className="flex-1">
                <p className="text-xs font-medium text-neutral-700">Manual entry key</p>
                <pre className="mt-1 overflow-x-auto rounded bg-neutral-100 p-2 font-mono text-xs">
                  {state.secretBase32}
                </pre>
                <p className="mt-2 text-xs text-neutral-500">
                  Account: <code>funkihoo-admin</code> · Algorithm: <code>SHA1</code> · Digits:{' '}
                  <code>6</code> · Interval: <code>30s</code>
                </p>
              </div>
            </div>
          </Section>

          <Section title="2. Confirm with the 6-digit code">
            <form
              className="space-y-2"
              onSubmit={async (e) => {
                e.preventDefault();
                setError(null);
                try {
                  const res = await activate.mutateAsync({
                    secretBase32: state.secretBase32,
                    code,
                  });
                  setState({ kind: 'activated', recoveryCodes: res.recoveryCodes });
                  setCode('');
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Could not activate');
                }
              }}
            >
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                placeholder="123456"
                className="w-40 rounded-md border border-neutral-300 px-3 py-2 text-center font-mono text-lg tracking-widest"
              />
              {error && <p className="text-sm text-danger-600">{error}</p>}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={activate.isPending || code.length !== 6}
                  className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {activate.isPending ? 'Activating…' : 'Activate'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setState({ kind: 'idle' });
                    setCode('');
                    setError(null);
                  }}
                  className="rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-700"
                >
                  Cancel
                </button>
              </div>
            </form>
          </Section>
        </div>
      )}

      {state.kind === 'activated' && <RecoveryCodesPanel codes={state.recoveryCodes} />}

      <DisableCard />
      <RotateCard />
    </section>
  );
}

// ─── Disable card ────────────────────────────────────────────────────

function DisableCard() {
  const [code, setCode] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const disable = useTotpDisableMutation();

  return (
    <details className="rounded-md border border-neutral-200 bg-white p-4">
      <summary className="cursor-pointer font-medium text-neutral-900 text-sm">
        Disable TOTP
      </summary>
      <p className="mt-2 text-neutral-600 text-sm">
        Turns off two-factor authentication. Requires your current TOTP code or one of your recovery
        codes. Production admin sign-in won't work until you re-enroll.
      </p>
      <form
        className="mt-3 space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          disable.mutate({ currentCode: code });
        }}
      >
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="6-digit code or recovery code"
          autoComplete="off"
          className="w-72 rounded-md border border-neutral-300 px-3 py-2 font-mono text-sm"
        />
        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          I understand I can't sign in to admin until I re-enroll.
        </label>
        {disable.error && (
          <p className="text-sm text-danger-600">{(disable.error as Error).message}</p>
        )}
        {disable.data?.ok && (
          <p className="text-sm text-success-700">
            TOTP disabled. Re-enroll above to turn it back on.
          </p>
        )}
        <button
          type="submit"
          disabled={disable.isPending || code.length === 0 || !confirmed}
          className="rounded-md bg-danger-600 px-3 py-2 text-sm font-medium text-white hover:bg-danger-700 disabled:opacity-50"
        >
          {disable.isPending ? 'Disabling…' : 'Disable TOTP'}
        </button>
      </form>
    </details>
  );
}

// ─── Rotate card ─────────────────────────────────────────────────────

/** RFC 4648 Base32 alphabet (RFC 6238 §4 specifies Base32 secrets for
 *  TOTP). 32 chars; padding stripped. */
const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

/** Generate a random 20-byte Base32 secret, the size RFC 6238 §4
 *  recommends for HMAC-SHA1 + 160-bit security. Browser-only —
 *  uses crypto.getRandomValues. */
function generateBase32Secret(): string {
  const bytes = new Uint8Array(20);
  crypto.getRandomValues(bytes);
  let bits = 0;
  let value = 0;
  let out = '';
  for (const b of bytes) {
    value = (value << 8) | b;
    bits += 8;
    while (bits >= 5) {
      out += BASE32_ALPHABET[(value >>> (bits - 5)) & 0x1f];
      bits -= 5;
    }
  }
  if (bits > 0) {
    out += BASE32_ALPHABET[(value << (5 - bits)) & 0x1f];
  }
  return out;
}

function buildOtpauthUri(secret: string, issuer = 'funkihoo', label = 'admin'): string {
  const params = new URLSearchParams({
    secret,
    issuer,
    algorithm: 'SHA1',
    digits: '6',
    period: '30',
  });
  return `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(label)}?${params.toString()}`;
}

type RotateState =
  | { kind: 'idle' }
  | { kind: 'staged'; newSecret: string; otpauthUri: string }
  | { kind: 'done'; recoveryCodes: string[] };

function RotateCard() {
  const [state, setState] = useState<RotateState>({ kind: 'idle' });
  const [currentCode, setCurrentCode] = useState('');
  const [newCode, setNewCode] = useState('');
  const rotate = useTotpRotateMutation();

  return (
    <details className="rounded-md border border-neutral-200 bg-white p-4">
      <summary className="cursor-pointer font-medium text-neutral-900 text-sm">
        Rotate TOTP secret
      </summary>
      <p className="mt-2 text-neutral-600 text-sm">
        Replace your current TOTP secret with a fresh one. Use this when switching authenticators.
        The old secret + recovery codes stop working immediately on success.
      </p>

      {state.kind === 'idle' && (
        <button
          type="button"
          onClick={() => {
            const newSecret = generateBase32Secret();
            setState({
              kind: 'staged',
              newSecret,
              otpauthUri: buildOtpauthUri(newSecret),
            });
          }}
          className="mt-3 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Generate new secret
        </button>
      )}

      {state.kind === 'staged' && (
        <div className="mt-3 space-y-3">
          <Section title="1. Scan the new secret with your authenticator">
            <div className="flex items-start gap-3">
              <QrFallback uri={state.otpauthUri} />
              <div className="flex-1">
                <p className="text-xs font-medium text-neutral-700">Manual entry key</p>
                <pre className="mt-1 overflow-x-auto rounded bg-neutral-100 p-2 font-mono text-xs">
                  {state.newSecret}
                </pre>
              </div>
            </div>
          </Section>

          <Section title="2. Enter your CURRENT TOTP code">
            <input
              type="text"
              value={currentCode}
              onChange={(e) => setCurrentCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              placeholder="123456"
              className="w-40 rounded-md border border-neutral-300 px-3 py-2 text-center font-mono text-lg"
            />
          </Section>

          <Section title="3. Enter the NEW TOTP code (from the just-added entry)">
            <input
              type="text"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              className="w-40 rounded-md border border-neutral-300 px-3 py-2 text-center font-mono text-lg"
            />
          </Section>

          {rotate.error && (
            <p className="text-sm text-danger-600">{(rotate.error as Error).message}</p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={rotate.isPending || currentCode.length !== 6 || newCode.length !== 6}
              onClick={async () => {
                try {
                  const res = await rotate.mutateAsync({
                    currentCode,
                    newSecretBase32: state.newSecret,
                    newCode,
                  });
                  setState({ kind: 'done', recoveryCodes: res.recoveryCodes });
                  setCurrentCode('');
                  setNewCode('');
                } catch {
                  // Error surfaced via rotate.error above.
                }
              }}
              className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {rotate.isPending ? 'Rotating…' : 'Rotate'}
            </button>
            <button
              type="button"
              onClick={() => {
                setState({ kind: 'idle' });
                setCurrentCode('');
                setNewCode('');
              }}
              className="rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {state.kind === 'done' && (
        <div className="mt-3">
          <RecoveryCodesPanel codes={state.recoveryCodes} />
        </div>
      )}
    </details>
  );
}

function RecoveryCodesPanel({ codes }: { codes: string[] }) {
  const [confirmed, setConfirmed] = useState(false);
  return (
    <div className="rounded-md border border-warning-200 bg-warning-50 p-4">
      <h2 className="font-semibold text-neutral-900">Save your recovery codes</h2>
      <p className="mt-1 text-sm text-neutral-700">
        Each code works once. Use them if you lose access to your authenticator. We'll never show
        these again — save them now to your password manager or print them.
      </p>
      <ul className="mt-3 grid grid-cols-2 gap-2">
        {codes.map((c) => (
          <li
            key={c}
            className="rounded bg-white px-3 py-2 font-mono text-sm text-neutral-900 shadow-sm"
          >
            {c}
          </li>
        ))}
      </ul>
      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(codes.join('\n'));
          }}
          className="rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-700 hover:bg-white"
        >
          Copy all
        </button>
        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          I've saved these somewhere safe
        </label>
      </div>
      {confirmed && (
        <p className="mt-3 text-sm text-success-700">
          Done — TOTP is active on your account. Future sign-ins will prompt for a code.
        </p>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="font-medium text-sm text-neutral-900">{title}</h3>
      <div className="mt-2">{children}</div>
    </section>
  );
}

/**
 * Minimal QR fallback. Renders the otpauth:// URI as text and as a
 * link the admin can copy into a QR generator (or their authenticator
 * if it accepts pasted URIs). When a `qrcode` library lands in deps,
 * swap this for a real <canvas>/<svg> QR. The text fallback is fine for
 * the launch threshold — every authenticator supports manual-key entry,
 * and the URI is one tap to copy.
 */
function QrFallback({ uri }: { uri: string }) {
  return (
    <div className="flex h-40 w-40 shrink-0 flex-col items-center justify-center rounded border border-neutral-200 bg-neutral-50 p-2 text-center">
      <p className="text-xs font-medium text-neutral-700">QR placeholder</p>
      <p className="mt-1 text-xs text-neutral-500">
        Copy the manual key →
        <br />
        or paste the otpauth URI:
      </p>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText(uri);
        }}
        className="mt-2 rounded bg-neutral-200 px-2 py-1 text-xs text-neutral-700 hover:bg-neutral-300"
      >
        Copy otpauth URI
      </button>
    </div>
  );
}
